import { useQuery } from '@tanstack/react-query'
import {
  extractExchangeRate, extractFrankfurterExchangeRate, getCurrency, getEcbSeriesKey, isPublishedByEcb,
} from '../models/mapper'
import { fetchCurrencyExchangeData, fetchFrankfurterExchangeData } from './http'
import ExchangeRateUnavailableError from '../models/ExchangeRateUnavailableError'

interface FetchOptions {
  signal?: AbortSignal
}

async function fetchEcbExchangeRate(currency: string, opts?: FetchOptions): Promise<number> {
  const { data } = await fetchCurrencyExchangeData(getEcbSeriesKey(currency), opts)
  return extractExchangeRate(data)
}

async function fetchFrankfurterExchangeRate(currency: string, opts?: FetchOptions): Promise<number> {
  const { data } = await fetchFrankfurterExchangeData(currency, opts)
  return extractFrankfurterExchangeRate(data, currency)
}

export async function fetchCurrencyExchangeRate(country: string | null, opts?: FetchOptions): Promise<number> {
  const currency = getCurrency(country)
  if (!currency) {
    return 0
  }

  if (isPublishedByEcb(currency)) {
    try {
      return await fetchEcbExchangeRate(currency, opts)
    } catch (e) {
      if (opts?.signal?.aborted) {
        throw e
      }
      // eslint-disable-next-line no-console
      console.warn('[exchange-rate] ECB lookup failed, falling back to Frankfurter', e)
    }
  }

  try {
    return await fetchFrankfurterExchangeRate(currency, opts)
  } catch (e) {
    if (opts?.signal?.aborted) {
      throw e
    }
    throw new ExchangeRateUnavailableError(currency, { cause: e })
  }
}

export function useCurrencyExchangeRate(country: string | null) {
  return useQuery({
    enabled: !!country,
    queryFn: ({ signal }) => fetchCurrencyExchangeRate(country as string, { signal }),
    queryKey: ['currency-exchange-rate', country],
    retry: 1,
    staleTime: Infinity,
  })
}
